import { DeviceDTO } from '@eppendorf/vnls-device-service-utils';
import { t } from 'i18next';
import { useCallback, useState } from 'react';
import { Trans } from 'react-i18next';

import { ConfirmDeleteDialog } from '$components/confirm-delete-dialog/confirm-delete-dialog';
import { ErrorDeleteRetryDialog } from '$components/confirm-delete-dialog/error-delete-retry-dialog';
import { SuccessDeleteDialog } from '$components/confirm-delete-dialog/success-delete-dialog';
import { useDeleteDevice } from '$features/devices/devices.api';
import styles from '$features/notifications/dialog-with-ul.module.scss';

function useDeleteDeviceHandler() {
  const { mutateAsync } = useDeleteDevice();
  return useCallback(
    async (device: DeviceDTO | undefined) => {
      if (!device) return;
      await mutateAsync(device);
    },
    [mutateAsync],
  );
}

export interface ConfirmDeleteDeviceWrapperProps {
  selectedDevice?: DeviceDTO;
  onOpenChange: (open: boolean) => void;
  isDeleteConfirmOpen: boolean;
}

function getDescriptionByDeviceType(type?: string) {
  switch (type) {
    case 'ESS1000001':
      return 'removeDevice.confirmDeleteDescription.gateway';
    case 'ESS1000002':
      return 'removeDevice.confirmDeleteDescription.monitor';
    case '1008':
    case '1006':
      return 'removeDevice.confirmDeleteDescription.box';
    default:
      return 'removeDevice.confirmDeleteDescription.generic';
  }
}

function Description({ selectedDevice }: { selectedDevice?: DeviceDTO }) {
  const typeDescription = getDescriptionByDeviceType(selectedDevice?.type);
  return (
    <div className={`text-align-left ${styles.dialogWithUl}`}>
      <p>{t('removeDevice.confirmDeleteDescription.top')}</p>
      <ul>
        {t(`${typeDescription}.bullet1`) && (
          <li>
            <Trans
              i18nKey={`${typeDescription}.bullet1`}
              components={{ strong: <span className="font-weight-bold" /> }}
            />
          </li>
        )}
        {t(`${typeDescription}.bullet2`) && (
          <li>
            <Trans
              i18nKey={`${typeDescription}.bullet2`}
              components={{ strong: <span className="font-weight-bold" /> }}
            />
          </li>
        )}
        {t(`${typeDescription}.bullet3`) && (
          <li>
            <Trans
              i18nKey={`${typeDescription}.bullet3`}
              components={{ strong: <span className="font-weight-bold" /> }}
            />
          </li>
        )}
        {t(`${typeDescription}.bullet4`) && (
          <li>
            <Trans
              i18nKey={`${typeDescription}.bullet4`}
              components={{ strong: <span className="font-weight-bold" /> }}
            />
          </li>
        )}
      </ul>
    </div>
  );
}

export function DeleteDeviceConfirmWrapper({
  isDeleteConfirmOpen = false,
  onOpenChange,
  selectedDevice,
}: ConfirmDeleteDeviceWrapperProps) {
  const [isErrorDeleteDeviceDialogOpen, setIsErrorDeleteDeviceDialogOpen] =
    useState(false);
  const [isSuccessDeleteDeviceDialogOpen, setIsSuccessDeleteDeviceDialogOpen] =
    useState(false);

  const deleteDevice = useDeleteDeviceHandler();

  async function onConfirmDeleteFunction(device: DeviceDTO | undefined) {
    try {
      await deleteDevice(device);
    } catch (error) {
      onOpenChange(false);
      setIsErrorDeleteDeviceDialogOpen(true);
      return;
    }

    onOpenChange(false);
    setIsSuccessDeleteDeviceDialogOpen(true);
  }

  return (
    <>
      <ConfirmDeleteDialog
        isOpen={isDeleteConfirmOpen}
        onClose={() => onOpenChange(false)}
        onConfirmDelete={() => onConfirmDeleteFunction(selectedDevice)}
        title={t('removeDevice.title')}
        description={<Description selectedDevice={selectedDevice} />}
      />
      <ErrorDeleteRetryDialog
        isOpen={isErrorDeleteDeviceDialogOpen}
        onClose={() => setIsErrorDeleteDeviceDialogOpen(false)}
        onTryAgain={() => onConfirmDeleteFunction(selectedDevice)}
        title={t('errors.errorOccurred')}
        message={t('errors.unexpected')}
      />
      <SuccessDeleteDialog
        isOpen={isSuccessDeleteDeviceDialogOpen}
        onClose={() => setIsSuccessDeleteDeviceDialogOpen(false)}
        title={t('removeDevice.successDialog.title')}
        description={t('removeDevice.successDialog.message')}
      />
    </>
  );
}
